import { MAX_RESOURCE_QUERY_DAYS } from '../allocations.js';
import { addDays, daysBetween, isoDate, monthOf, weekOf } from '../calendar/dates.js';
import { bucketStart, type Granularity } from './capacity.js';

// Colour levels and ranges of the heatmap (RES-05, phase 7c decisions).

/**
 * `none`: neither capacity nor bookings (weekend, holiday, absence). Otherwise booked ÷ available:
 * free below 50 %, normal 50–89 %, full 90–100 %, over above 100 % (also bookings without capacity).
 */
export type UtilizationLevel = 'none' | 'free' | 'normal' | 'full' | 'over';

export function utilizationLevel(booked: number, available: number): UtilizationLevel {
  if (available <= 0) return booked > 0 ? 'over' : 'none';
  // Compare in hundredths to avoid floating point noise (e.g. 7.2 of 8 h = 90 %).
  const b = Math.round(booked * 100);
  const a = Math.round(available * 100);
  if (b > a) return 'over';
  if (b * 10 >= a * 9) return 'full';
  if (b * 2 >= a) return 'normal';
  return 'free';
}

/**
 * Level of a project view cell (RES-09), which only knows the total as percentage: overbooked
 * on any day of the bucket wins, otherwise the thresholds above.
 */
export function utilizationLevelOfPercent(
  totalPercent: number | null,
  booked: number,
  overbooked: boolean,
): UtilizationLevel {
  if (overbooked) return 'over';
  if (totalPercent === null) return booked > 0 ? 'over' : 'none';
  if (totalPercent > 100) return 'over';
  if (totalPercent >= 90) return 'full';
  if (totalPercent >= 50) return 'normal';
  return 'free';
}

/** Booked ÷ available in whole percent; null without capacity. */
export function utilizationPercent(booked: number, available: number): number | null {
  return available > 0 ? Math.round((booked / available) * 100) : null;
}

export type HeatmapRange = { from: string; to: string };

/** Last day of the bucket starting at or containing `date`. */
function bucketEnd(date: string, granularity: Granularity): string {
  if (granularity === 'week') return weekOf(date).to;
  if (granularity === 'month') return monthOf(date).to;
  return date;
}

/** Default view: weeks, the current week and the 12 following (decision 7c). */
export function defaultHeatmapRange(today: string): HeatmapRange {
  const from = weekOf(today).from;
  return { from, to: addDays(from, 13 * 7 - 1) };
}

/**
 * Aligns a range to whole buckets and limits it to the maximum query length; `to` before `from`
 * becomes a single bucket.
 */
export function alignRange(range: HeatmapRange, granularity: Granularity): HeatmapRange {
  const from = bucketStart(range.from, granularity);
  let to = bucketEnd(range.to < range.from ? range.from : range.to, granularity);
  const max = addDays(from, MAX_RESOURCE_QUERY_DAYS - 1);
  if (to > max) {
    // Cut back to the last whole bucket that still fits.
    to = bucketEnd(max, granularity);
    if (to > max) to = addDays(bucketStart(max, granularity), -1);
  }
  return { from, to };
}

function addMonths(date: string, months: number): string {
  const [year = 0, month = 1] = date.split('-').map(Number);
  return isoDate(year, month + months, 1);
}

/** Moves an aligned range by its own length (one page) backwards (-1) or forwards (+1). */
export function shiftRange(
  range: HeatmapRange,
  granularity: Granularity,
  direction: -1 | 1,
): HeatmapRange {
  if (granularity === 'month') {
    const from = monthOf(range.from).from;
    const months =
      (Number(range.to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 +
      Number(range.to.slice(5, 7)) -
      Number(from.slice(5, 7)) +
      1;
    const start = addMonths(from, direction * months);
    return { from: start, to: monthOf(addMonths(start, months - 1)).to };
  }
  const days = daysBetween(range.from, range.to) + 1;
  return { from: addDays(range.from, direction * days), to: addDays(range.to, direction * days) };
}
